import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { Platform } from "react-native";
import { DialogWeb } from "./dialog-web";
import { DialogNative } from "./dialog-native";
export const DialogsContext = React.createContext([]);
const ShowDialogFnContext = React.createContext(null);
export const useShowDialog = () => {
    const showDialog = React.useContext(ShowDialogFnContext);
    if (!showDialog) {
        throw new Error("DialogProvider not in parent chain");
    }
    return showDialog;
};
export const useShowConfirm = () => {
    const showDialog = React.useContext(ShowDialogFnContext);
    if (!showDialog) {
        throw new Error("DialogProvider not in parent chain");
    }
    const showConfirmationDialog = React.useCallback(async (opts) => {
        return await new Promise((resolve) => {
            showDialog({
                title: opts.title,
                message: opts.message,
                confirmLabel: opts.confirmLabel,
                onConfirm: () => resolve(true),
                onCancel: () => resolve(false),
            });
        });
    }, [showDialog]);
    return showConfirmationDialog;
};
/**
 * @deprecated use `useShowConfirm` instead
 */
export const useShowConfirmationDialog = useShowConfirm;
export const useShowAlert = () => {
    const showDialog = React.useContext(ShowDialogFnContext);
    if (!showDialog) {
        throw new Error("DialogProvider not in parent chain");
    }
    const showAlertDialog = React.useCallback(async (opts) => {
        return await new Promise((resolve) => {
            showDialog({
                title: opts.title,
                message: opts.message,
                buttons: [
                    {
                        label: "Okay",
                        type: "primary",
                        onClick: () => resolve(),
                    }
                ]
            });
        });
    }, [showDialog]);
    return showAlertDialog;
};
/**
 * @deprecated use `useShowAlert` instead
 */
export const useAlertDialog = useShowAlert;
/**
 * Be careful about using this directly, you should typically use useShowDialog, useShowConfirm, or useShowAlert.
 *
 * This is the underlying component that actually renders the dialog.
 */
export const Dialog = Platform.OS === 'web' ? DialogWeb : DialogNative;
export const DialogProvider = React.memo(function DialogProvider(props) {
    const [dialogs, setDialogs] = React.useState([]);
    const showDialog = React.useCallback((dialog) => {
        const id = Math.random().toString();
        const remove = () => {
            setDialogs((dialogs) => dialogs.filter((d) => d.id !== id));
        };
        setDialogs((dialogs) => [
            ...dialogs,
            'buttons' in dialog ? {
                id,
                title: dialog.title,
                message: dialog.message,
                extras: dialog.extras,
                icon: dialog.icon,
                buttons: dialog.buttons,
                zIndex: dialogs.length + 1,
                onClose: async () => {
                    remove();
                },
            } : {
                ...dialog,
                id,
                buttons: [
                    {
                        label: dialog.confirmLabel ?? "Confirm",
                        onClick: "submit",
                        type: "primary",
                    },
                    {
                        label: "Cancel",
                        onClick: () => {
                            if (dialog.onCancel) {
                                dialog.onCancel();
                            }
                        },
                        type: "secondary",
                    }
                ],
                onClose: async () => {
                    remove();
                    await dialog.onCancel?.();
                },
                onConfirm: async (value) => {
                    if ("onConfirm" in dialog) {
                        await dialog.onConfirm();
                    }
                    else {
                        await dialog.onSubmit(value ?? "");
                    }
                    remove();
                },
                zIndex: dialogs.length + 1,
            },
        ]);
    }, []);
    return (_jsx(DialogsContext.Provider, { value: dialogs, children: _jsxs(ShowDialogFnContext.Provider, { value: showDialog, children: [props.children, dialogs.map((dialog) => (_jsx(Dialog, { ...dialog }, dialog.id)))] }) }));
});
